import i18n from 'i18next';
import { pollingAPI } from 'api/api';
import { APIResponse } from './types';

export interface APIResponsePooling<T> {
    status: 'OK' | 'ERROR' | 'FAILED' | 'QUEUE';
    data: T;
    message?: string;
}

interface CreateResponse {
    ppl_id: number;
}

export interface PoolingRequestData {
    poolingCreateURL?: string;
    poolingCreateMethod?: 'GET' | 'POST';
}

export interface PoolingType<T, D> {
    onSuccess: (data: T, responsePoolingData: any) => any;
    onError: (data: T, responsePoolingData: any) => any;
    onPplIdChange?: (pplId: number) => any;
    requestData: D & PoolingRequestData;
}

const CHECK_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 5 * 60 * 1000;

export default function pollingService<T extends any, D>({
    onSuccess,
    onError,
    requestData,
    onPplIdChange,
}: PoolingType<T, D>) {
    let isActive = true;
    let timeOutId: number | null = null;
    let canceled = false;
    let responsePoolingData: any = null;
    let startedAt: number | null = null;

    const finish = (callback: (data: any, responsePoolingData: any) => any, data: any) => {
        if (!isActive) return;
        isActive = false;
        if (timeOutId) {
            window.clearTimeout(timeOutId);
            timeOutId = null;
        }
        if (onPplIdChange) {
            onPplIdChange(0);
        }
        callback(data, responsePoolingData);
    };

    const checker = function (pplId: number) {
        if (!isActive) return;

        timeOutId = window.setTimeout(() => {
            if (!isActive) return;

            if (startedAt !== null && Date.now() - startedAt > MAX_WAIT_MS) {
                finish(onError, { message: i18n.t('polling_still_running') });
                return;
            }

            pollingAPI
                .check(pplId)
                .then(({ data }: { data: APIResponse<T> }) => {
                    if (data.status == 'OK') {
                        finish(onSuccess, data.data);
                    } else if (data.status == 'ERROR' || data.status == 'FAILED') {
                        finish(onError, data.data);
                    } else {
                        checker(pplId);
                    }
                })
                .catch(function () {
                    finish(onError, { message: i18n.t('polling_check_failed') });
                });
        }, CHECK_INTERVAL_MS);
    };

    const create = () => {
        canceled = false;
        startedAt = Date.now();
        pollingAPI
            .create(requestData)
            .then(({ data }: { data: APIResponsePooling<CreateResponse> }) => {
                if (canceled) return;
                if (data?.status == 'ERROR') {
                    const errorData: any = data?.data ?? { message: data?.message ?? '' };
                    finish(onError, errorData);
                } else {
                    responsePoolingData = data?.data;
                    if (onPplIdChange) {
                        onPplIdChange(data.data.ppl_id);
                    }
                    run(data.data.ppl_id);
                }
            })
            .catch(function () {
                finish(onError, { message: i18n.t('polling_create_failed') });
            });
    };

    const run = (pplId: number) => {
        startedAt = startedAt ?? Date.now();
        pollingAPI.run(pplId);
        timeOutId = window.setTimeout(() => checker(pplId), 1000);
    };

    const cancel = (pplId: number) => {
        canceled = true;
        finish(onError, { message: i18n.t('polling_canceled') });
        if (pplId > 0) {
            pollingAPI.cancel(pplId);
        }
    };

    return {
        create,
        run,
        cancel,
    };
}
